import React, { useState, useEffect } from 'react';
import { MessageSquare } from 'lucide-react';
import ReviewCard from './UserReviewsComponents/ReviewCard';
import ReviewOverview from './UserReviewsComponents/ReviewOverview';
import { ReviewProvider, useReviewContext } from '../../context/ReviewContext';

const UserReviewsContent = () => {
  const { reviews, loading, error, getReviews } = useReviewContext();
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token_partner_restaurant');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const businessId = payload.business_id;
        if (businessId) {
          getReviews('Restaurant', businessId, token);
        } else {
          setAuthError('Business identifier not found in token');
        }
      } catch (e) {
        setAuthError('Error parsing authentication info');
      }
    } else {
      setAuthError('Authentication required');
    }
  }, [getReviews]);

  const calculateAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  const calculateRatingStats = () => {
    const stats = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    if (reviews.length === 0) return stats;
    reviews.forEach(r => {
      const rating = Math.round(r.rating);
      if (stats[rating] !== undefined) stats[rating]++;
    });
    Object.keys(stats).forEach(key => {
      stats[key] = (stats[key] / reviews.length) * 100;
    });
    return stats;
  };

  if (loading)
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <p className="text-gray-500 font-medium animate-pulse">Loading reviews...</p>
      </div>
    );

  if (authError || error) return <div className="text-rose-500 text-center p-4 font-bold">{authError || error}</div>;

  return (
    <div className="p-4 sm:p-6 space-y-8 animate-in fade-in duration-700">
      <ReviewOverview 
        averageRating={calculateAverageRating()} 
        totalReviews={reviews.length} 
        ratingStats={calculateRatingStats()} 
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reviews.length > 0 ? (
          reviews.map((review) => (
            <ReviewCard key={review._id} review={review} />
          ))
        ) : (
          <div className="col-span-full py-24 bg-white/40 backdrop-blur-md rounded-[3rem] border border-dashed text-center">
            <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-bold text-gray-800">No reviews yet</h3>
            <p className="text-gray-500 text-sm mt-2">Feedback and ratings from your guests will appear here.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const UserReviews = () => (
  <ReviewProvider>
    <UserReviewsContent />
  </ReviewProvider>
);

export default UserReviews;
